import React, { useEffect, useRef, useState } from 'react';
import { useUserData } from 'context/UserDataProvider';
import { Stack, Flex, Box, useToast, Spinner, Text, VStack } from '@chakra-ui/react';
import ZaraButton from '../components/custom-components/buttons/Button';
import CreateAgentFormWrapper from './create-agent/components/CreateAgentFormWrapper';
import useServerApi from '../hooks/useServerApi';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../router/routes';

import WhoReferred from '../components/page-components/company-details/WhoReferred';


const loadGoogleMapsScript = (apiKey) => {
    return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
            resolve(window.google.maps);
            return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.google.maps);
        script.onerror = reject;

        document.head.appendChild(script);
    });
};


const CompanyDetails = () => {
    const inputRef = useRef(null);
    const toast = useToast();
    const [parsedData, setParsedData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const [progressMessage, setProgressMessage] = useState('');
    const [showReferred, setShowReferred] = useState(false);
    const [{ post, get }] = useServerApi();
    const [inputValue, setInputValue] = useState('');
    const [placesBlurred, setPlacesBlurred] = useState(false);
    const [placesData, setPlacesData] = useState({});
    const [websiteValue, setWebsiteValue] = useState('');
    const [fullAddressValue, setFullAddressValue] = useState('');
    const [googleMapsKey, setGoogleMapsKey] = useState(null);
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const router = useNavigate();
    const { fetchUserData, userData } = useUserData();

    useEffect(() => {
        // Fetch Google Maps API key from backend
        const fetchGoogleMapsKey = async () => {
            try {
                const [ok, res] = await get('/rest_endpoints/accounts/service_configuration/google_place_id');
                if (ok && res.key) {
                    setGoogleMapsKey(res.key);
                } else {
                    console.error('Failed to fetch Google Maps API key', res);
                }
            } catch (err) {
                console.error('Error fetching Google Maps API key', err);
            }
        };
        fetchGoogleMapsKey();
    }, [get]);

    async function loadPlace(placeName) {
        const { Place } = await window.google.maps.importLibrary("places");
        const request = {
            textQuery: placeName,
            //fields: ["displayName", "location", "businessStatus", "formattedAddress", "types", "websiteURI", "id"],
            fields: ["*"],
            language: "en-US",
            maxResultCount: 8,
            minRating: 3.2,
            region: "us",
            useStrictTypeFiltering: false,
        };

        const { places } = await Place.searchByText(request);


        if (places.length > 0) {
            const place = places[0];
            const extractedData = {
                name: place.displayName || '',
                website: place.websiteURI || '',
                fullAddress: place.formattedAddress || '',
                placeId: place.id,
                types: place.types,
                openingHours: place.regularOpeningHours?.weekdayText || 'Not available',
            }
            setParsedData(extractedData);
            setInputValue(place.displayName || '');
            setWebsiteValue(place.websiteURI || '');
            setFullAddressValue(place.formattedAddress || '');
            setPlacesData(extractedData);
            setPlacesBlurred(true); // Set blurred state to true after loading place
        }

    }

    useEffect(() => {
        // Only load script after key is available
        if (!googleMapsKey || scriptLoaded) return;

        loadGoogleMapsScript(googleMapsKey).then(() => {
            setScriptLoaded(true);
        });
    }, [googleMapsKey, scriptLoaded]);

    useEffect(() => {
        // Initialize autocomplete only after script is loaded
        if (!scriptLoaded) return;
        if (window.google && inputRef.current) {
            const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current);
            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                const data = {
                    name: place.name,
                    website: place.website || '',
                    fullAddress: place.formatted_address || '',
                    openingHours: place.regularOpeningHours?.weekday_text || 'Not available',
                    placeId: place.place_id,
                    types: place.types,
                };
                setParsedData(data);
                setInputValue(place.name || '');
                setWebsiteValue(place.website || '');
                setFullAddressValue(place.formatted_address || '');
                setPlacesData(place);
            });
            if (userData?.user_info?.company?.name) {
                // Load place data if company name is available
                loadPlace(userData.user_info.company.name);
            }
        } else {
            console.error('Google Maps JavaScript API is not loaded or inputRef missing.');
        }

    }, [scriptLoaded, userData?.user_info?.company?.name]);

    const pollAgentStatus = async (taskId, onSuccess) => {
        const pollInterval = 3000;

        const progressMessages = [
            'Analyzing your business information...',
            'Setting up your AI receptionist...',
            'Customizing voice and personality...',
            'Integrating business details...',
            'Finalizing your agent setup...',
            'Almost ready! Preparing your AI agent...'
        ];
        const checkStatus = async () => {
            try {
                setIsPolling(true);
                const [ok, res] = await get(`/rest_endpoints/agents/crud/status/${taskId}`);
                if (ok && res.status === 'SUCCESS') {
                    setProgressMessage('Agent created successfully!');
                    setTimeout(() => {
                        setIsPolling(false);
                        if (onSuccess) onSuccess();
                    }, pollInterval);
                } else if (ok && res.status === 'FAILURE') {
                    setIsPolling(false);
                    toast({
                        status: 'error',
                        title: 'Unable to complete task',
                        isClosable: true,
                    });
                } else if (ok && res.status === 'PROGRESS' && res.meta?.message) {
                    // Use server-provided progress message when available
                    if (progressMessage !== res.meta.message) {
                        setProgressMessage(res.meta.message);
                    }
                    setTimeout(checkStatus, pollInterval);
                    return
                }
                else {
                    setProgressMessage(progressMessages[1]);
                    setTimeout(checkStatus, pollInterval);
                }

            } catch (err) {
                setIsPolling(false);
                console.error('Error polling agent status', err);
            }
        };
        checkStatus();
    };

    // Handles agent creation and status polling
    const saveAndCreateAgent = async (data) => {
        setIsLoading(true);
        setProgressMessage('Revving up your AI receptionist...');
        setShowReferred(true);
        try {
            const [success, response] = await post('/rest_endpoints/agents/crud/create', data);
            if (success && response.task_id) {
                pollAgentStatus(response.task_id, async () => {
                    try {
                        await fetchUserData();
                    } catch (err) {
                        console.error('Error fetching user data before navigation', err);
                    }
                    router(PATHS.MY_AGENT);
                });
            } else {
                // handle error if needed
            }
        } catch (error) {
            console.error('Error submitting data:', error);
        } finally {
            setIsLoading(false);
            setParsedData(null);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Compose data from form fields, fallback to parsedData for autocomplete fields
        const data = {
            ...parsedData,
            name: inputValue,
            website: websiteValue,
            fullAddress: fullAddressValue,
            placesData: placesData,
        };
        // Require name and (website or full address)
        if (inputValue && (websiteValue || fullAddressValue)) {
            saveAndCreateAgent(data);
        }
    };

    return (
        <CreateAgentFormWrapper
            title={isPolling ? "" : "Tell us about your business"}
            description={isPolling ? "" : "We'll use this information to customize your AI receptionist."}
        >
            <Box position="relative">
                {(isPolling || !scriptLoaded) && (
                    <VStack justify="center" align="center" spacing={4} my={4}>
                        <Spinner color="#5612F8" />
                        <Text color="gray.600" textAlign="center">
                            {isPolling ? progressMessage : 'Loading business directory...'}
                        </Text>
                    </VStack>
                )}

                {/* Show WhoReferred overlay when showReferred is true, regardless of polling */}
                {(showReferred) && (
                    <WhoReferred
                        onSkip={() => setShowReferred(false)}
                    />
                )}

                {!isPolling && scriptLoaded && (
                    <Stack as="form" onSubmit={handleSubmit} spacing="3">
                        {/* ...existing code... */}
                        <label htmlFor="company-location" style={{ fontWeight: 500, marginBottom: 4 }}>
                            Company Name or Location
                        </label>
                        <input
                            id="company-location"
                            ref={inputRef}
                            type="text"
                            value={inputValue}
                            onChange={e => {
                                setPlacesBlurred(false);
                                setPlacesData({});
                                const value = e.target.value;
                                setInputValue(value);
                                if (!value) {
                                    setWebsiteValue("");
                                    setFullAddressValue("");
                                }
                            }}
                            onBlur={() => {
                                setPlacesBlurred(true);
                            }}
                            placeholder="Start typing your company name or address..."
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #E2E8F0',
                                borderRadius: 6,
                                marginBottom: 8,
                                fontSize: 16,
                            }}
                        />

                        {placesBlurred && (inputValue.length > 2 || websiteValue || fullAddressValue) && (
                            <>
                                <label htmlFor="company-website" style={{ fontWeight: 500, marginBottom: 4 }}>
                                    Website
                                </label>
                                <input
                                    id="company-website"
                                    type="text"
                                    value={websiteValue}
                                    onChange={e => setWebsiteValue(e.target.value)}
                                    placeholder="Enter your company website (optional)"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #E2E8F0',
                                        borderRadius: 6,
                                        marginBottom: 8,
                                        fontSize: 16,
                                    }}
                                />
                                <label htmlFor="company-full-address" style={{ fontWeight: 500, marginBottom: 4 }}>
                                    Full Address
                                </label>
                                <input
                                    id="company-full-address"
                                    type="text"
                                    value={fullAddressValue}
                                    onChange={e => setFullAddressValue(e.target.value)}
                                    placeholder="Enter the full address (optional)"
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        border: '1px solid #E2E8F0',
                                        borderRadius: 6,
                                        marginBottom: 8,
                                        fontSize: 16,
                                    }}
                                />
                            </>
                        )}
                        <Flex justifyContent="flex-end">
                            <ZaraButton
                                isDisabled={
                                    !inputValue || (!websiteValue && !fullAddressValue) || isLoading
                                }
                                isLoading={isLoading || isPolling}
                                type="button"
                                variant="blackAlpha"
                                onClick={handleSubmit}
                            >
                                Next
                            </ZaraButton>
                        </Flex>
                    </Stack>
                )}
                {/* Overlay removed: no visual blocking during polling */}
            </Box>
        </CreateAgentFormWrapper>
    );
};

export default CompanyDetails;